import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';

interface UpdateMaterielElectriqueProps {
  t: any;
  isRTL: boolean;
  onClose: () => void;
  onSuccess: () => void;
  productId: string;
}

interface MaterielElectriqueProduct {
  id?: string;
  name: string;
  title?: string;
  mainImage: string;
  images?: string[];
  category: string;
  subcategory: string;
  description?: string;
  characteristics?: { [key: string]: string };
  additionalImages: string[];
  createdAt?: any;
  updatedAt?: any;
}

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)<{ $isRTL: boolean }>`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
`;

const ModalTitle = styled.h2<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Label = styled.label<{ $isRTL: boolean }>`
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const Input = styled.input<{ $isRTL: boolean }>`
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Select = styled.select<{ $isRTL: boolean }>`
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const TextArea = styled.textarea<{ $isRTL: boolean }>`
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
  min-height: 100px;
  resize: vertical;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const CharacteristicsContainer = styled.div<{ $isRTL: boolean }>`
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const CharacteristicsGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns}, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CharacteristicCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CharacteristicInput = styled(Input)`
  width: 100%;
`;

const CharacteristicLabel = styled.label<{ $isRTL: boolean }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const CharacteristicActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-top: 0.5rem;
`;

const RemoveCharacteristicButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background-color 0.2s ease;

  &:hover {
    background: #dc2626;
  }
`;

const AddCharacteristicButton = styled.button`
  background: #10b981;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background-color 0.2s ease;
  margin-bottom: 1rem;

  &:hover:not(:disabled) {
    background: #059669;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const GridControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const ColumnControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ColumnInput = styled.input`
  width: 60px;
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 2px solid #e5e7eb;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${props => props.$variant === 'primary' ? `
    background: #3b82f6;
    color: white;
    
    &:hover {
      background: #2563eb;
    }
    
    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  ` : `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  `}
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const FileInput = styled.input<{ $isRTL: boolean }>`
  padding: 0.75rem;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.3s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ImagePreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(220, 38, 38, 1);
  }
`;

const UploadProgress = styled.div<{ $progress: number }>`
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;

  &::after {
    content: '';
    display: block;
    width: ${props => props.$progress}%;
    height: 100%;
    background: linear-gradient(90deg, #3b82f6, #1d4ed8);
    transition: width 0.3s ease;
  }
`;

const ProgressText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.5rem 0 0 0;
  text-align: center;
`;

const UpdateMaterielElectrique: React.FC<UpdateMaterielElectriqueProps> = ({ 
  t, isRTL, onClose, onSuccess, productId 
}) => {
  const [formData, setFormData] = useState<MaterielElectriqueProduct>({
    name: '',
    title: '',
    mainImage: '',
    images: [],
    category: '2',
    subcategory: '',
    description: '',
    characteristics: {},
    additionalImages: []
  });

  const [characteristics, setCharacteristics] = useState<Array<{ key: string; value: string }>>([]);
  const [loading, setLoading] = useState(false);
  // Add state for grid columns
  const [gridColumns, setGridColumns] = useState(2);

  const subcategories = [
    { value: 'solutions', label: 'Solutions' },
    { value: 'isolateurs-composites', label: 'Isolateurs Composites HTA / HTB' },
    { value: 'accessoires-chaines', label: 'Accessoires pour Chaînes d\'Isolateurs' },
    { value: 'interrupteur-aerien', label: 'Interrupteur Aérien à Commande Manuelle IACM' },
    { value: 'accessoires-reseaux-bt', label: 'Accessoires pour Réseaux B.T' },
    { value: 'accessoires-protection', label: 'Accessoires de protection et raccordement' },
    { value: 'ferrure-armement', label: 'Ferrure et Armement' },
    { value: 'eclairage-public', label: 'Eclairage Public' }
  ];

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = useCallback(async () => {
    try {
      setLoading(true);
      const product = await FirebaseService.getProduct(productId);
      if (product) {
        // Cast the product to the expected type since Firebase returns { id, ...data }
        const typedProduct = product as MaterielElectriqueProduct;
        setFormData(typedProduct);
        
        // Convert characteristics object to array
        if (typedProduct.characteristics) {
          const characteristicsArray = Object.entries(typedProduct.characteristics).map(([key, value]) => ({
            key,
            value: value as string
          }));
          setCharacteristics(characteristicsArray);
        }
      }
    } catch (error) {
      console.error('Error loading product:', error);
      alert('Erreur lors du chargement du produit');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCharacteristicChange = (index: number, field: 'key' | 'value', value: string) => {
    const newCharacteristics = [...characteristics];
    newCharacteristics[index][field] = value;
    setCharacteristics(newCharacteristics);
  };

  const addCharacteristic = () => {
    if (characteristics.length < 8) {
      setCharacteristics([...characteristics, { key: '', value: '' }]);
    }
  };

  const removeCharacteristic = (index: number) => {
    setCharacteristics(characteristics.filter((_, i) => i !== index));
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Convert characteristics array to object
      const characteristicsObj: { [key: string]: string } = {};
      characteristics.forEach(char => {
        if (char.key && char.value) {
          characteristicsObj[char.key] = char.value;
        }
      });

      const productData = {
        ...formData,
        characteristics: characteristicsObj,
        updatedAt: new Date()
      };

      await FirebaseService.updateProduct(productId, productData);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating Matériel Électrique product:', error);
      alert('Erreur lors de la mise à jour du produit');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AnimatePresence>
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            $isRTL={isRTL}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
              <p>Chargement du produit...</p>
            </div>
          </ModalContent>
        </ModalOverlay>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContent
          $isRTL={isRTL}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle $isRTL={isRTL}>Modifier le produit Matériel Électrique</ModalTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit}>
            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Nom du produit *</Label>
              <Input
                $isRTL={isRTL}
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Titre (optionnel)</Label>
              <Input
                $isRTL={isRTL}
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Sous-catégorie *</Label>
              <Select
                $isRTL={isRTL}
                name="subcategory"
                value={formData.subcategory}
                onChange={handleInputChange}
                required
              >
                <option value="">Sélectionner une sous-catégorie</option>
                {subcategories.map(sub => (
                  <option key={sub.value} value={sub.value}>
                    {sub.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Image principale URL *</Label>
              <Input
                $isRTL={isRTL}
                type="url"
                name="mainImage"
                value={formData.mainImage}
                onChange={handleInputChange}
                required
                placeholder="https://example.com/image.jpg"
              />
              {formData.mainImage && (
                <ImagePreviewContainer>
                  <ImagePreview>
                    <img src={formData.mainImage} alt="Main product" />
                  </ImagePreview>
                </ImagePreviewContainer>
              )}
            </FormGroup>

            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Images supplémentaires (URLs séparées par des virgules)</Label>
              <TextArea
                $isRTL={isRTL}
                name="additionalImages"
                value={formData.additionalImages.join(', ')}
                onChange={(e) => {
                  const urls = e.target.value.split(',').map(url => url.trim()).filter(url => url);
                  setFormData(prev => ({ ...prev, additionalImages: urls }));
                }}
                placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                rows={3}
              />
              {formData.additionalImages && formData.additionalImages.length > 0 && (
                <ImagePreviewContainer>
                  {formData.additionalImages.map((image, index) => (
                    <ImagePreview key={index}>
                      <img src={image} alt={`Additional product view ${index + 1}`} />
                    </ImagePreview>
                  ))}
                </ImagePreviewContainer>
              )}
            </FormGroup>

            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Description</Label>
              <TextArea
                $isRTL={isRTL}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Description du produit"
              />
            </FormGroup>

            <CharacteristicsContainer $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Caractéristiques Techniques</Label>
              
              <GridControls>
                <ColumnControl>
                  <label>Colonnes:</label>
                  <ColumnInput
                    type="number"
                    min="1"
                    max="8"
                    value={gridColumns}
                    onChange={(e) => setGridColumns(Math.max(1, Math.min(8, parseInt(e.target.value) || 1)))}
                  />
                </ColumnControl>
                <AddCharacteristicButton 
                  type="button" 
                  onClick={addCharacteristic}
                  disabled={characteristics.length >= 8}
                >
                  + Ajouter une caractéristique ({characteristics.length}/8)
                </AddCharacteristicButton>
              </GridControls>
              
              <CharacteristicsGrid $columns={gridColumns}>
                {characteristics.map((char, index) => (
                  <CharacteristicCell key={index}>
                    <CharacteristicLabel $isRTL={isRTL}>
                      Nom de la caractéristique
                    </CharacteristicLabel>
                    <CharacteristicInput
                      $isRTL={isRTL}
                      type="text"
                      placeholder="Nom de la caractéristique"
                      value={char.key}
                      onChange={(e) => handleCharacteristicChange(index, 'key', e.target.value)}
                    />
                    <CharacteristicLabel $isRTL={isRTL}>
                      Valeur
                    </CharacteristicLabel>
                    <CharacteristicInput
                      $isRTL={isRTL}
                      type="text"
                      placeholder="Valeur"
                      value={char.value}
                      onChange={(e) => handleCharacteristicChange(index, 'value', e.target.value)}
                    />
                    <CharacteristicActions>
                      <RemoveCharacteristicButton
                        type="button"
                        onClick={() => removeCharacteristic(index)}
                      >
                        Supprimer
                      </RemoveCharacteristicButton>
                    </CharacteristicActions>
                  </CharacteristicCell>
                ))}
              </CharacteristicsGrid>
            </CharacteristicsContainer>

            <ButtonGroup>
              <Button type="button" onClick={onClose}>
                Annuler
              </Button>
              <Button type="submit" $variant="primary" disabled={loading}>
                {loading ? (
                  <>
                    <LoadingSpinner />
                    Mise à jour en cours...
                  </>
                ) : (
                  'Mettre à jour le produit'
                )}
              </Button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </ModalOverlay>
    </AnimatePresence>
  );
};

export default UpdateMaterielElectrique;
